import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import TopNavigation from './TopNavigation';
import BottomNavigation from './BottomNavigation';
import Onboarding from './Onboarding';
import { useOnboarding } from '../hooks/useOnboarding';
import { Icon } from './Icon';
import ProtocolReport from './ProtocolReport';
import ProtocolShield from './ProtocolShield';
import BiohackInfoCard from './BiohackInfoCard';
import JellyfishLoader from './JellyfishLoader';
import { enrichBiomarkerWithRanges } from '../utils/biomarkerUtils';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { getUserFacingErrorMessage } from '../utils/sanitizeErrorForLog';
import './InsightsIndex.css';
import './ReportModal.css';
import './AttachmentSelector.css';
import './ProtocolSelector.css';

// Wrapper component to load example snapshot from database
const ExampleReportWrapper = ({ onClose }) => {
	const [exampleSnapshotId, setExampleSnapshotId] = useState(null);
	const [loading, setLoading] = useState(true);
	
	useEffect(() => {
		const loadExampleSnapshot = async () => {
			try {
				// Load the example snapshot from database by title
				// The snapshot is created with a dummy user ID for onboarding
				// RLS policies should allow viewing if the snapshot is marked as example/onboarding
				const { data, error } = await supabase
					.from('protocol_report_snapshots')
					.select('id')
					.eq('title', 'Morning Energy Protocol Report')
					.order('created_at', { ascending: false })
					.limit(1)
					.maybeSingle();
				
				if (error) {
					console.error('Error loading example snapshot:', error);
					// If RLS blocks, the error will indicate it
					if (error.code === 'PGRST301' || error.message?.includes('permission')) {
						console.warn('RLS policy may be blocking access to example snapshot');
					}
				} else if (data && data.id) {
					setExampleSnapshotId(data.id);
				} else {
					console.warn('Example snapshot not found. Make sure the SQL script ran successfully.');
				}
			} catch (e) {
				console.error('Exception loading example snapshot:', e);
			} finally {
				setLoading(false);
			}
		};
		
		loadExampleSnapshot();
	}, []);
	
	if (loading) {
		return <JellyfishLoader />;
	}
	
	if (!exampleSnapshotId) {
		return (
			<div style={{ padding: '2rem', textAlign: 'center' }}>
				<p>Example report not found. Please run the SQL script to create it.</p>
				<button className="btn btn--primary" onClick={onClose}>
					Close
				</button>
			</div>
		);
	}
	
	// Use the snapshot ID to load the report normally
	return (
		<ProtocolReport 
			reportId={exampleSnapshotId}
			isModal={true}
			onClose={onClose}
			isExampleReport={true}
		/>
	);
};

const InsightsIndex = () => {
	const navigate = useNavigate();
	const [protocolOptions, setProtocolOptions] = useState([]);
	const [reports, setReports] = useState([]);
	const [loading, setLoading] = useState(true);
	const [userProfile, setUserProfile] = useState({ avatarUrl: '', userName: '' });
	const [userGender, setUserGender] = useState(null);
	const [showReportModal, setShowReportModal] = useState(false);
	const [modalReportId, setModalReportId] = useState(null);
	const [modalProtocolId, setModalProtocolId] = useState(null);
	const [openFabMenuId, setOpenFabMenuId] = useState(null);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [reportToDelete, setReportToDelete] = useState(null);
	const [showProtocolSelectorCard, setShowProtocolSelectorCard] = useState(false);
	const [isCreatingReport, setIsCreatingReport] = useState(false);
	const [protocolsForSelector, setProtocolsForSelector] = useState([]);
	const [loadingProtocols, setLoadingProtocols] = useState(false);
	const [selectedBiohack, setSelectedBiohack] = useState(null);
	const [protocolSelectorSearchTerm, setProtocolSelectorSearchTerm] = useState('');
	const [showProtocolSelectorSearch, setShowProtocolSelectorSearch] = useState(false);
	const protocolSelectorContentRef = useRef(null);
	const protocolSelectorLastTouchY = useRef(null);
	const [scrollProgress, setScrollProgress] = useState(0);
	const [cardPositions, setCardPositions] = useState({});
	const reportRowRef = React.useRef(null);
	const cardRefs = React.useRef({});
	const isLoadingDataRef = useRef(false);
	const [showTipsModal, setShowTipsModal] = useState(false);
	
	// Disclaimer modal state
	const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
	const [disclaimerText, setDisclaimerText] = useState('');
	const [disclaimerVersion, setDisclaimerVersion] = useState('');
	const [acceptResponsibility, setAcceptResponsibility] = useState(false);
	const [doNotShowAgain, setDoNotShowAgain] = useState(false);
	const [pendingProtocol, setPendingProtocol] = useState(null);
	
	// First-time insights onboarding state
	const [currentUserId, setCurrentUserId] = useState(null);
	
	// Use centralized onboarding hook
	const [showOnboarding, setShowOnboarding] = useOnboarding({
		shouldCheck: !!currentUserId,
		userId: currentUserId
	});

	useEffect(() => {
		loadData();
		
		// Get current user ID for onboarding check
		const getUserId = async () => {
			const { data: { session } } = await supabase.auth.getSession();
			if (session?.user) {
				setCurrentUserId(session.user.id);
			}
		};
		getUserId();
		
		// Listen for custom event to show example report from onboarding
		const handleShowExampleReportEvent = () => {
			// Show example report modal
			setModalReportId('example');
			setModalProtocolId(null);
			setShowReportModal(true);
			sessionStorage.setItem('seen_insights_onboarding', 'true');
			// Trigger custom event to update navigation notification
			window.dispatchEvent(new CustomEvent('onboardingProgressUpdate'));
		};
		
		window.addEventListener('onboardingShowExampleReport', handleShowExampleReportEvent);
		
		return () => {
			window.removeEventListener('onboardingShowExampleReport', handleShowExampleReportEvent);
		};
	}, []);

	// Special step advancement logic for Insights page
	useEffect(() => {
		if (showOnboarding && currentUserId) {
			// Only show step 3 (insights) if user has completed step 2 and is on insights page
			const hasSeenBiohacksOnboarding = sessionStorage.getItem('seen_biohacks_onboarding');
			if (hasSeenBiohacksOnboarding) {
				try {
					const saved = localStorage.getItem('onboarding_progress');
					if (saved) {
						const parsed = JSON.parse(saved);
						// If step is 2 or less, advance to step 3 (insights step)
						if (parsed.step <= 2) {
							localStorage.setItem('onboarding_progress', JSON.stringify({
								step: 2,
								timestamp: new Date().toISOString()
							}));
						}
					}
				} catch (e) {
					// Invalid progress, ignore
				}
			}
		}
	}, [showOnboarding, currentUserId]);
	
	const loadData = async () => {
		// Prevent duplicate concurrent calls (e.g., from React StrictMode)
		if (isLoadingDataRef.current) {
			return;
		}
		
		try {
			isLoadingDataRef.current = true;
			setLoading(true);
			// Use getSession() instead of getUser() to avoid network requests
			const { data: { session } } = await supabase.auth.getSession();
			const user = session?.user;
			if (!user) {
				setProtocolOptions([]);
				setReports([]);
				return;
			}

		// Load user profile data for report covers
		// Use same cache key as TopNavigation and Home to share the request
		try {
		const profileCacheKey = getCacheKey('fetchUserProfile', user.id);
		const profileRow = await dedupeRequest(profileCacheKey, async () => {
			const { data, error } = await supabase
				.from('user_profiles')
				.select('username, profile_picture_url, gender')
				.eq('user_id', user.id)
				.maybeSingle();
			if (error) throw error;
			return data;
		}, { caller: 'InsightsIndex' });
		setUserProfile({
			userName: profileRow?.username || '',
			avatarUrl: profileRow?.profile_picture_url || ''
			});
		setUserGender(profileRow?.gender);
		} catch (e) {
			console.error('Failed to load user profile:', e);
		}

			// Fetch user's active protocols
			const { data: statusRows } = await supabase
				.from('protocols_status')
				.select(`
					id,
					is_active,
					added_at,
					activated_at,
					deactivated_at,
					protocols:protocol_id (
						id, name, description
					)
				`)
				.eq('user_id', user.id)
				.eq('is_active', true)
				.order('activated_at', { ascending: false });

			const options = (statusRows || [])
				.map(r => r.protocols)
				.filter(Boolean)
				.reduce((acc, p) => {
					if (!acc.find(x => x.id === p.id)) acc.push(p);
					return acc;
				}, []);
			setProtocolOptions(options);

			// Fetch existing report snapshots (historical reports)
			try {
				// Use deduplication to prevent duplicate requests
				const snapshotsCacheKey = getCacheKey('fetchProtocolReportSnapshots', user.id);
				const snapshotRows = await dedupeRequest(snapshotsCacheKey, async () => {
					const { data, error } = await supabase
						.from('protocol_report_snapshots')
						.select(`
							id,
							protocol_id,
							title,
							snapshot_date,
							created_at,
							stats,
							protocols:protocol_id ( name )
						`)
						.eq('user_id', user.id)
						.order('snapshot_date', { ascending: false });
					
					if (error) throw error;
					return data;
				});
				
				if (!snapshotRows) {
					setReports([]);
					// Check if we need to add dummy report for onboarding
					// This will be handled after checkInsightsOnboarding completes
					return;
				}
				
				// Transform snapshots to match the expected report format
				// Use snapshot_id as the primary identifier for viewing historical reports
				const transformedReports = snapshotRows.map(snapshot => ({
					id: snapshot.id, // Use snapshot id as the primary identifier
					snapshot_id: snapshot.id, // Keep for reference
					protocol_id: snapshot.protocol_id,
					title: snapshot.title,
					created_at: snapshot.snapshot_date || snapshot.created_at, // Use snapshot_date as the report date
					metrics: snapshot.stats, // Use stats from snapshot
					protocols: snapshot.protocols
				}));
				
				setReports(transformedReports);
				
			} catch (e) {
				console.error('[REPORT HISTORY] Exception loading snapshots:', e);
				console.error('[REPORT HISTORY] Exception details:', {
					message: e?.message,
					stack: e?.stack,
					name: e?.name
				});
				setReports([]);
				// Check if we need to add dummy report for onboarding
				// This will be handled after checkInsightsOnboarding completes
			}
		} finally {
			setLoading(false);
			isLoadingDataRef.current = false;
		}
	};

	// Function to collect all report data and create snapshot
	const createReportSnapshot = async (protocol, userId) => {
		try {
			// Get protocol status
			const { data: statusRow } = await supabase
				.from('protocols_status')
				.select('activated_at, deactivated_at, is_active')
				.eq('user_id', userId)
				.eq('protocol_id', protocol.id)
				.order('activated_at', { ascending: false })
				.limit(1)
				.single();

			// Calculate stats
			let loggedCount = 0;
			try {
				// Use deduplication to prevent duplicate requests
				const countCacheKey = getCacheKey('countLoggingHackEntries', `${userId}_${protocol.id}`);
				const logsCount = await dedupeRequest(countCacheKey, async () => {
					const { count, error } = await supabase
						.from('logging_hack_entries')
						.select('id', { count: 'exact', head: true })
						.eq('user_id', userId)
						.eq('protocol_id', protocol.id);
					if (error) throw error;
					return count || 0;
				});
				loggedCount = logsCount;
			} catch (err) {
				console.error('[SNAPSHOT CREATE] Exception counting logs:', err);
			}

			let daysActive = 0;
			if (statusRow?.activated_at) {
				const start = new Date(statusRow.activated_at);
				const end = statusRow?.is_active && !statusRow?.deactivated_at
					? new Date()
					: (statusRow?.deactivated_at ? new Date(statusRow.deactivated_at) : new Date());
				const ms = Math.max(0, end - start);
				daysActive = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
			}

			let daysWithLogs = 0;
			try {
				// Use deduplication to prevent duplicate requests
				const datesCacheKey = getCacheKey('fetchLoggingHackEntryDates', `${userId}_${protocol.id}`);
				const logRows = await dedupeRequest(datesCacheKey, async () => {
					const { data, error } = await supabase
						.from('logging_hack_entries')
						.select('date')
						.eq('user_id', userId)
						.eq('protocol_id', protocol.id);
					if (error) throw error;
					return data;
				});
				
				if (Array.isArray(logRows) && logRows.length > 0) {
					// date is already in YYYY-MM-DD format, so we can use it directly
					const daySet = new Set(logRows.map(r => r.date).filter(Boolean));
					daysWithLogs = daySet.size;
				}
			} catch (err) {
				console.error('[SNAPSHOT CREATE] Exception loading log rows:', err);
			}

			// Get protocol with hacks
			const { data: protocolWithHacks } = await supabase
				.from('protocols')
				.select('*, protocol_hacks(hack_id, position)')
				.eq('id', protocol.id)
				.single();

			const totalBiohacks = Array.isArray(protocolWithHacks?.protocol_hacks)
				? protocolWithHacks.protocol_hacks.length
				: 0;

			const adherencePct = daysActive > 0
				? Math.max(0, Math.min(100, Math.round((daysWithLogs / daysActive) * 100)))
				: 0;

			const stats = { loggedCount, daysActive, daysWithLogs, adherencePct, totalBiohacks };

			// Load activity data
			let allEntries = [];
			try {
				// Use deduplication to prevent duplicate requests
				const activityCacheKey = getCacheKey('fetchLoggingHackEntryActivity', `${userId}_${protocol.id}`);
				const loggingRows = await dedupeRequest(activityCacheKey, async () => {
					const { data, error } = await supabase
						.from('logging_hack_entries')
						.select('date, hack_id')
						.eq('user_id', userId)
						.eq('protocol_id', protocol.id)
						.order('date', { ascending: true });
					if (error) throw error;
					return data;
				});
				
				allEntries = Array.isArray(loggingRows) ? loggingRows.filter(r => r?.date && r?.hack_id) : [];
			} catch (err) {
				console.error('[SNAPSHOT CREATE] Exception loading logging rows:', err);
			}
			
			const tally = {};
			for (const row of allEntries) {
				const key = row.hack_id;
				tally[key] = (tally[key] || 0) + 1;
			}

			const today = new Date();
			const start = new Date(today);
			start.setDate(today.getDate() - 27);
			const startStr = start.toISOString().slice(0, 10);
			const todayStr = today.toISOString().slice(0, 10);
			const entries = allEntries.filter(e => e.date >= startStr && e.date <= todayStr);

			const activityData = {
				entries,
				allEntries,
				tally,
				windowStart: start.toISOString()
			};

			// Load biohack countries using database function (bypasses RLS for aggregate data)
			const hackIds = Array.isArray(protocolWithHacks?.protocol_hacks)
				? protocolWithHacks.protocol_hacks.map(ph => ph.hack_id).filter(Boolean)
				: [];
			
			const biohackCountries = {};
			if (hackIds.length > 0) {
				try {
					// Use database function to get aggregate country statistics
					// This bypasses RLS to get data from all users (aggregate only)
					const sortedHackIds = [...hackIds].sort().join(',');
					const countriesCacheKey = getCacheKey('getBiohackCountriesAggregate', sortedHackIds);
					const countryData = await dedupeRequest(countriesCacheKey, async () => {
					const { data, error } = await supabase.rpc('get_biohack_countries_aggregate', {
						hack_ids: hackIds
					});
						if (error) {
							console.error('[SNAPSHOT CREATE] RPC error getting biohack countries:', error);
							throw error;
						}
						console.log('[SNAPSHOT CREATE] Biohack countries data:', data);
						return data || [];
					});
					
					// Convert to object format for snapshot storage
					if (countryData && countryData.length > 0) {
						countryData.forEach(row => {
							if (row.hack_id && row.country) {
								biohackCountries[row.hack_id] = { 
									country: row.country, 
									count: Number(row.count) || 0 
								};
							}
						});
						console.log('[SNAPSHOT CREATE] Processed biohack countries:', biohackCountries);
					} else {
						console.warn('[SNAPSHOT CREATE] No country data returned for hack IDs:', hackIds);
					}
				} catch (err) {
					console.error('[SNAPSHOT CREATE] Error in biohack countries section:', err);
					// Continue without country data rather than failing the entire report
				}
			} else {
				console.warn('[SNAPSHOT CREATE] No hack IDs found for biohack countries');
			}

			// Load biomarkers
			const windowStart = statusRow?.activated_at ? new Date(statusRow.activated_at) : new Date();
			const reportEnd = new Date();
			const deactivated = statusRow?.deactivated_at ? new Date(statusRow.deactivated_at) : null;
			const windowEnd = deactivated && deactivated < reportEnd ? deactivated : reportEnd;

			const startISO = windowStart.toISOString().slice(0, 10);
			const endISO = windowEnd.toISOString().slice(0, 10);

			// Use deduplication to prevent duplicate requests
			const panelsUpToEndCacheKey = getCacheKey('fetchBiomarkerPanelsUpToEnd', `${userId}_${endISO}`);
			const panelsUpToEnd = await dedupeRequest(panelsUpToEndCacheKey, async () => {
				const { data, error } = await supabase
					.from('biomarker_panel_results')
					.select(`
						id, test_date,
						biomarker_results (*, biomarkers (*))
					`)
					.eq('user_id', userId)
					.lte('test_date', endISO)
					.order('test_date', { ascending: true });
				if (error) throw error;
				return data;
			});

			const beforePanelsCacheKey = getCacheKey('fetchBiomarkerPanelsBeforeStart', `${userId}_${startISO}`);
			const beforePanels = await dedupeRequest(beforePanelsCacheKey, async () => {
				const { data, error } = await supabase
					.from('biomarker_panel_results')
					.select(`
						id, test_date,
						biomarker_results (*, biomarkers (*))
					`)
					.eq('user_id', userId)
					.lt('test_date', startISO)
					.order('test_date', { ascending: false })
					.limit(50);
				if (error) throw error;
				return data;
			});

			const seriesByBiomarker = new Map();
			(panelsUpToEnd || []).forEach(panel => {
				(panel.biomarker_results || []).forEach(r => {
					const b = r.biomarkers;
					if (!b) return;
					const key = r.biomarker_id;
					if (!seriesByBiomarker.has(key)) {
						// Enrich biomarker with gender-specific ranges
						const enrichedBiomarker = enrichBiomarkerWithRanges(b, userGender);
						seriesByBiomarker.set(key, {
							id: key,
							name: enrichedBiomarker.name,
							unit: r.unit_override || enrichedBiomarker.unit || '',
							clinical_low: enrichedBiomarker.clinical_low,
							clinical_high: enrichedBiomarker.clinical_high,
							category: enrichedBiomarker.category,
							points: [],
							hasInRange: false
						});
					}
					const point = { date: panel.test_date, value: r.value };
					const record = seriesByBiomarker.get(key);
					record.points.push(point);
					if (!record.hasInRange) {
						const d = new Date(panel.test_date);
						const testDateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
						const windowStartOnly = new Date(windowStart.getFullYear(), windowStart.getMonth(), windowStart.getDate());
						const windowEndOnly = new Date(windowEnd.getFullYear(), windowEnd.getMonth(), windowEnd.getDate());
						if (testDateOnly >= windowStartOnly && testDateOnly <= windowEndOnly) {
							record.hasInRange = true;
						}
					}
				});
			});

			const baselineByBiomarker = new Map();
			(beforePanels || []).forEach(panel => {
				(panel.biomarker_results || []).forEach(r => {
					const key = r.biomarker_id;
					if (baselineByBiomarker.has(key)) return;
					baselineByBiomarker.set(key, {
						date: panel.test_date,
						value: r.value,
						unit: r.unit_override || r.biomarkers?.unit || ''
					});
				});
			});

			const biomarkerCards = Array.from(seriesByBiomarker.values())
				.filter(s => s.hasInRange)
				.map(s => {
					const sorted = [...s.points].sort((a, b) => new Date(a.date) - new Date(b.date));
					const latestPoint = sorted.length > 0 ? sorted[sorted.length - 1] : null;
					const baseline = baselineByBiomarker.get(s.id) || null;
				return {
					id: s.id,
					name: s.name,
					unit: s.unit,
					latest: latestPoint,
					baseline,
					series: sorted,
					clinical_low: s.clinical_low,
					clinical_high: s.clinical_high,
					category: s.category,
					status: null // Will be computed in the component
				};
				}).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

			// Load wearables
			// Use deduplication to prevent duplicate requests
			const sessionsUpToEndCacheKey = getCacheKey('fetchSleepSessionsUpToEnd', userId);
			const sessionsUpToEnd = await dedupeRequest(sessionsUpToEndCacheKey, async () => {
				const { data, error } = await supabase
					.from('sleep_sessions')
					.select('*')
					.eq('user_id', userId)
					.order('session_start', { ascending: true });
				if (error) throw error;
				return data;
			});

			const beforeSessionsCacheKey = getCacheKey('fetchSleepSessionsBeforeStart', `${userId}_${windowStart.toISOString().slice(0, 10)}`);
			const beforeSessions = await dedupeRequest(beforeSessionsCacheKey, async () => {
				const { data, error } = await supabase
					.from('sleep_sessions')
					.select('*')
					.eq('user_id', userId)
					.lt('session_start', windowStart.toISOString())
					.order('session_start', { ascending: false })
					.limit(50);
				if (error) throw error;
				return data;
			});

			const metrics = [
				{ id: 'total_sleep', name: 'Total Sleep', unit: 'h', getValue: (s) => (s.total_sleep_minutes || 0) / 60 },
				{ id: 'deep_sleep', name: 'Deep Sleep', unit: 'h', getValue: (s) => (s.deep_sleep_minutes || 0) / 60 },
				{ id: 'rem_sleep', name: 'REM Sleep', unit: 'h', getValue: (s) => (s.rem_sleep_minutes || 0) / 60 },
				{ id: 'light_sleep', name: 'Light Sleep', unit: 'h', getValue: (s) => (s.light_sleep_minutes || 0) / 60 },
				{ id: 'sleep_efficiency', name: 'Sleep Efficiency', unit: '%', getValue: (s) => s.sleep_efficiency ? s.sleep_efficiency * 100 : null },
				{ id: 'sleep_score', name: 'Sleep Score', unit: '', getValue: (s) => s.sleep_score }
			];

			const seriesByMetric = new Map();
			metrics.forEach(metric => {
				seriesByMetric.set(metric.id, {
					id: metric.id,
					name: metric.name,
					unit: metric.unit,
					points: [],
					hasInRange: false,
					getValue: metric.getValue
				});
			});

			(sessionsUpToEnd || []).forEach(session => {
				const sessionDate = new Date(session.session_start).toISOString().slice(0, 10);
				const sessionDateObj = new Date(session.session_start);
				const sessionDateOnly = new Date(sessionDateObj.getFullYear(), sessionDateObj.getMonth(), sessionDateObj.getDate());
				const windowStartOnly = new Date(windowStart.getFullYear(), windowStart.getMonth(), windowStart.getDate());
				const windowEndOnly = new Date(windowEnd.getFullYear(), windowEnd.getMonth(), windowEnd.getDate());
				const inWindow = sessionDateOnly >= windowStartOnly && sessionDateOnly <= windowEndOnly;

				metrics.forEach(metric => {
					const record = seriesByMetric.get(metric.id);
					const value = metric.getValue(session);
					if (value != null && value !== 0) {
						record.points.push({ date: sessionDate, value: value });
						if (!record.hasInRange && inWindow) {
							record.hasInRange = true;
						}
					}
				});
			});

			const baselineByMetric = new Map();
			(beforeSessions || []).forEach(session => {
				metrics.forEach(metric => {
					const key = metric.id;
					if (baselineByMetric.has(key)) return;
					const value = metric.getValue(session);
					if (value != null) {
						baselineByMetric.set(key, {
							date: new Date(session.session_start).toISOString().slice(0, 10),
							value: value,
							unit: metric.unit
						});
					}
				});
			});

			const wearablesCards = Array.from(seriesByMetric.values())
				.filter(s => s.hasInRange && s.points.length > 0)
				.map(s => {
					const sorted = [...s.points].sort((a, b) => new Date(a.date) - new Date(b.date));
					const latestPoint = sorted.length > 0 ? sorted[sorted.length - 1] : null;
					const baseline = baselineByMetric.get(s.id) || null;
					const changeAbs = latestPoint && baseline && typeof baseline.value === 'number' && typeof latestPoint.value === 'number'
						? +(latestPoint.value - baseline.value).toFixed(1)
						: null;
					return {
						id: s.id,
						name: s.name,
						unit: s.unit,
						latest: latestPoint,
						baseline,
						series: sorted,
						changeAbs
					};
				}).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

			// Calculate sleep composition
			const sessionsBeforeProtocol = (sessionsUpToEnd || []).filter(session => {
				const sessionDate = new Date(session.session_start);
				return sessionDate < windowStart;
			});

			let latestSessionBeforeStart = null;
			if (sessionsBeforeProtocol.length > 0) {
				latestSessionBeforeStart = sessionsBeforeProtocol.reduce((latest, session) => {
					const sessionDate = new Date(session.session_start);
					const latestDate = latest ? new Date(latest.session_start) : null;
					return !latestDate || sessionDate > latestDate ? session : latest;
				});
			}

			let sleepCompositionBefore = null;
			if (latestSessionBeforeStart) {
				const latestSessionDate = new Date(latestSessionBeforeStart.session_start);
				const sevenDaysBeforeLatest = new Date(latestSessionDate);
				sevenDaysBeforeLatest.setDate(sevenDaysBeforeLatest.getDate() - 7);
				const sevenDaysBeforeLatestISO = sevenDaysBeforeLatest.toISOString();

				const sessionsBeforeStart = (sessionsUpToEnd || []).filter(session => {
					const sessionDate = new Date(session.session_start);
					return sessionDate >= new Date(sevenDaysBeforeLatestISO) && sessionDate <= latestSessionDate;
				});

				let beforeDeep = 0, beforeRem = 0, beforeLight = 0, beforeAwake = 0;
				let beforeCount = 0;
				sessionsBeforeStart.forEach(session => {
					if (session.deep_sleep_minutes) beforeDeep += session.deep_sleep_minutes;
					if (session.rem_sleep_minutes) beforeRem += session.rem_sleep_minutes;
					if (session.light_sleep_minutes) beforeLight += session.light_sleep_minutes;
					if (session.awake_minutes) beforeAwake += session.awake_minutes;
					beforeCount++;
				});

				if (beforeCount > 0) {
					sleepCompositionBefore = {
						deep: beforeDeep / beforeCount / 60,
						rem: beforeRem / beforeCount / 60,
						light: beforeLight / beforeCount / 60,
						awake: beforeAwake / beforeCount / 60
					};
				}
			}

			let latestSessionOverall = null;
			if (sessionsUpToEnd && sessionsUpToEnd.length > 0) {
				latestSessionOverall = sessionsUpToEnd.reduce((latest, session) => {
					const sessionDate = new Date(session.session_start);
					const latestDate = latest ? new Date(latest.session_start) : null;
					return !latestDate || sessionDate > latestDate ? session : latest;
				});
			}

			let sleepCompositionRecent = null;
			if (latestSessionOverall) {
				const latestSessionDate = new Date(latestSessionOverall.session_start);
				const sevenDaysBeforeLatest = new Date(latestSessionDate);
				sevenDaysBeforeLatest.setDate(sevenDaysBeforeLatest.getDate() - 7);
				const sevenDaysBeforeLatestISO = sevenDaysBeforeLatest.toISOString();

				const sessionsAfterStart = (sessionsUpToEnd || []).filter(session => {
					const sessionDate = new Date(session.session_start);
					return sessionDate >= new Date(sevenDaysBeforeLatestISO) &&
					       sessionDate <= latestSessionDate &&
					       sessionDate >= windowStart;
				});

				let afterDeep = 0, afterRem = 0, afterLight = 0, afterAwake = 0;
				let afterCount = 0;
				sessionsAfterStart.forEach(session => {
					if (session.deep_sleep_minutes) afterDeep += session.deep_sleep_minutes;
					if (session.rem_sleep_minutes) afterRem += session.rem_sleep_minutes;
					if (session.light_sleep_minutes) afterLight += session.light_sleep_minutes;
					if (session.awake_minutes) afterAwake += session.awake_minutes;
					afterCount++;
				});

				if (afterCount > 0) {
					sleepCompositionRecent = {
						deep: afterDeep / afterCount / 60,
						rem: afterRem / afterCount / 60,
						light: afterLight / afterCount / 60,
						awake: afterAwake / afterCount / 60
					};
				}
			}

			// Prepare protocol snapshot (without biohack details - those are fetched from biohacks table)
			const protocolSnapshot = protocolWithHacks ? {
				id: protocolWithHacks.id,
				name: protocolWithHacks.name,
				description: protocolWithHacks.description,
				image_url: protocolWithHacks.image_url,
				created_by: protocolWithHacks.created_by,
				created_at: protocolWithHacks.created_at,
				updated_at: protocolWithHacks.updated_at,
				users_count: protocolWithHacks.users_count
			} : null;

			// Save snapshot
			const snapshotPayload = {
				user_id: userId,
				protocol_id: protocol.id,
				title: protocol.name || 'Protocol Report',
				snapshot_date: new Date().toISOString(),
				protocol_activated_at: statusRow?.activated_at || null,
				protocol_deactivated_at: statusRow?.deactivated_at || null,
				protocol_is_active: statusRow?.is_active || false,
				stats: stats,
				activity_data: activityData,
				biomarker_data: biomarkerCards,
				wearables_data: wearablesCards,
				sleep_composition_before: sleepCompositionBefore,
				sleep_composition_recent: sleepCompositionRecent,
				biohack_countries: biohackCountries,
				protocol_snapshot: protocolSnapshot
			};

			const { data: insertedData, error: snapshotError } = await supabase
				.from('protocol_report_snapshots')
				.insert(snapshotPayload)
				.select()
				.single();

			if (snapshotError) {
				console.error('[SNAPSHOT CREATE] Failed to save snapshot:', snapshotError);
				return null;
			}

			return insertedData?.id;
		} catch (error) {
			console.error('[SNAPSHOT CREATE] Error creating snapshot:', error);
			return null;
		}
	};

	// Check if user has accepted the protocol report creation disclaimer
	// Returns true if user has accepted (don't show modal), false if should show modal
	// Checks the most recent record since we always insert new rows
	const checkDisclaimerAcceptance = async (userId) => {
		if (!userId) {
			return false;
		}
		try {
			const { data, error } = await supabase
				.from('disclaimers_log')
				.select('id, accepted_at, do_not_show_again')
				.eq('user_id', userId)
				.eq('disclaimer_type', 'protocol_report_creation')
				.order('created_at', { ascending: false })
				.limit(1)
				.maybeSingle();

			if (error) {
				return false; // Show modal on error
			}

			// Show modal if no record exists (user hasn't accepted yet)
			if (!data) {
				return false; // Should show modal
			}
			
			// Don't show modal only if do_not_show_again is true
			// Even if user accepted before, if they didn't check "do not show again", show modal again
			if (data.do_not_show_again === true) {
				return true; // Don't show modal
			}
			
			// Show modal if do_not_show_again is false or null
			return false; // Should show modal
		} catch (error) {
			return false; // Show modal on error
		}
	};

	// Fetch disclaimer text from disclaimers table
	const fetchDisclaimerText = async () => {
		try {
			const { data, error } = await supabase
				.from('disclaimers')
				.select('text, version')
				.eq('type', 'protocol_report_creation')
				.eq('latest', true)
				.maybeSingle();
			
			if (error) {
				return null;
			}

			if (!data) {
				return null;
			}

			return data;
		} catch (error) {
			return null;
		}
	};

	// Store disclaimer acceptance - always inserts new row for audit trail
	const storeDisclaimerAcceptance = async (userId, version, doNotShow) => {
		if (!userId) return false;
		try {
			// Always insert a new record (creates audit trail)
			const { error } = await supabase
				.from('disclaimers_log')
				.insert({
					user_id: userId,
					disclaimer_type: 'protocol_report_creation',
					disclaimer_version: version,
					accepted_at: new Date().toISOString(),
					do_not_show_again: doNotShow
				});

			if (error) {
				console.error('Error storing disclaimer acceptance:', error);
				return false;
			}

			return true;
		} catch (error) {
			console.error('Error storing disclaimer acceptance:', error);
			return false;
		}
	};

	// Internal function to perform report creation (after disclaimer check)
	const createReportInternal = async (protocol) => {
		try {
			// Use getSession() instead of getUser() to avoid network requests
			const { data: { session } } = await supabase.auth.getSession();
			const user = session?.user;
			if (!user) {
				setIsCreatingReport(false);
				return;
			}

			// Create snapshot first
			const snapshotId = await createReportSnapshot(protocol, user.id);
			
			if (snapshotId) {
				// Open the report using the snapshot ID
				setModalReportId(snapshotId);
				setModalProtocolId(null);
				setShowReportModal(true);
				setIsCreatingReport(false);
			} else {
				// Fallback: open with protocol ID if snapshot creation failed
				setModalReportId(null);
				setModalProtocolId(protocol.id);
				setShowReportModal(true);
				setIsCreatingReport(false);
			}
		} catch (error) {
			console.error('[SNAPSHOT CREATE] Exception in handleCreateReport:', error);
			// Fallback: open with protocol ID
			setModalReportId(null);
			setModalProtocolId(protocol.id);
			setShowReportModal(true);
			setIsCreatingReport(false);
		}
	};

	const handleCreateReport = async (protocol) => {
		try {
			// Use getSession() instead of getUser() to avoid network requests
			const { data: { session } } = await supabase.auth.getSession();
			const user = session?.user;
			if (!user) {
				setIsCreatingReport(false);
				return;
			}

			// Check if user has accepted the disclaimer
			const hasAcceptedDisclaimer = await checkDisclaimerAcceptance(user.id);
			
			if (!hasAcceptedDisclaimer) {
				// Fetch disclaimer text and show modal
				const disclaimer = await fetchDisclaimerText();
				if (disclaimer && disclaimer.text) {
					setDisclaimerText(disclaimer.text);
					setDisclaimerVersion(disclaimer.version || 'v1.0');
					setPendingProtocol(protocol);
					setShowDisclaimerModal(true);
					setAcceptResponsibility(false);
					setDoNotShowAgain(false);
					setIsCreatingReport(false);
					return;
				}
			}

			// User has accepted disclaimer, proceed with report creation
			await createReportInternal(protocol);
		} catch (error) {
			console.error('Error creating report:', error);
			setIsCreatingReport(false);
		}
	};

	// Handle disclaimer acceptance
	const handleDisclaimerAccept = async () => {
		if (!acceptResponsibility) {
			// Required checkbox not checked
			return;
		}

		try {
			// Use getSession() instead of getUser() to avoid network requests
			const { data: { session } } = await supabase.auth.getSession();
			const user = session?.user;
			if (!user || !pendingProtocol) {
				setShowDisclaimerModal(false);
				setPendingProtocol(null);
				return;
			}

			// Store disclaimer acceptance in database
			const acceptanceStored = await storeDisclaimerAcceptance(user.id, disclaimerVersion, doNotShowAgain);
			
			if (acceptanceStored) {
				setShowDisclaimerModal(false);
				setAcceptResponsibility(false);
				setDoNotShowAgain(false);
				
				// User has accepted disclaimer, proceed with report creation
				setIsCreatingReport(true);
				await createReportInternal(pendingProtocol);
				setPendingProtocol(null);
			} else {
				alert('Error storing disclaimer acceptance. Please try again.');
			}
		} catch (error) {
			console.error('Error handling disclaimer acceptance:', error);
			alert('Error storing disclaimer acceptance. Please try again.');
			setIsCreatingReport(false);
		}
	};

	const handleDisclaimerCancel = () => {
		// Just close the modal, nothing changes
		setShowDisclaimerModal(false);
		setAcceptResponsibility(false);
		setDoNotShowAgain(false);
		setPendingProtocol(null);
		setIsCreatingReport(false);
	};

	const handleViewReport = (snapshotId) => {
		// snapshotId is actually the snapshot.id from the transformed reports
		setModalReportId(snapshotId);
		setModalProtocolId(null);
		setShowReportModal(true);
	};
	
	const handleCloseModal = () => {
		setShowReportModal(false);
		setModalReportId(null);
		setModalProtocolId(null);
		setIsCreatingReport(false);
		// Reload reports to show any newly created ones
		loadData();
	};

	const fetchProtocolsForSelector = async () => {
		try {
			setLoadingProtocols(true);
			// Use getSession() instead of getUser() to avoid network requests
			const { data: { session } } = await supabase.auth.getSession();
			const user = session?.user;
			if (!user) {
				setProtocolsForSelector([]);
				return;
			}

			// Fetch user's active protocols with full protocol data
			const { data: statusRows } = await supabase
				.from('protocols_status')
				.select(`
					id,
					is_active,
					protocols:protocol_id (
						id,
						name,
						description,
						created_at,
						created_by,
						pop_score,
						adherence_score,
						protocol_hacks (
							hack_id,
							position,
							biohacks (
								id,
								name,
								image_url
							)
						),
						protocol_ratings (
							rating
						),
						user_profiles!protocols_created_by_fkey (
							user_id,
							username
						)
					)
				`)
				.eq('user_id', user.id)
				.eq('is_active', true)
				.order('activated_at', { ascending: false });

			const protocols = (statusRows || [])
				.map(r => r.protocols)
				.filter(Boolean)
				.reduce((acc, p) => {
					if (!acc.find(x => x.id === p.id)) acc.push(p);
					return acc;
				}, [])
				.map(protocol => {
					// Add created_by_username to each protocol for ProtocolShield display
					const creatorUsername = protocol.user_profiles?.username || 'Unknown';
					return {
						...protocol,
						created_by_username: creatorUsername
					};
				});

			setProtocolsForSelector(protocols);
		} catch (error) {
			console.error('Error fetching protocols for selector:', error);
			setProtocolsForSelector([]);
		} finally {
			setLoadingProtocols(false);
		}
	};

	const handleCreateFabButtonClick = async (e) => {
		e.stopPropagation();
		if (!showProtocolSelectorCard) {
			await fetchProtocolsForSelector();
		}
		setShowProtocolSelectorCard(!showProtocolSelectorCard);
	};

	const handleProtocolSelect = (protocol) => {
		// Automatically create report when protocol is selected
		setIsCreatingReport(true);
		setShowProtocolSelectorCard(false);
		handleCreateReport(protocol);
	};

	const handleProtocolSelectorClose = () => {
		setShowProtocolSelectorCard(false);
	};

	const handleProtocolSelectorOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			handleProtocolSelectorClose();
		}
	};

	const handleProtocolSelectorOverlayWheel = (e) => {
		if (e.target === e.currentTarget) {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	const handleProtocolSelectorContentWheel = (e) => {
		const content = protocolSelectorContentRef.current;
		if (!content) return;

		const { scrollTop, scrollHeight, clientHeight } = content;
		const isAtTop = scrollTop === 0;
		const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
		
		if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
			e.preventDefault();
			e.stopPropagation();
		} else {
			e.stopPropagation();
		}
	};

	const handleProtocolSelectorContentTouchStart = (e) => {
		if (e.touches.length > 0) {
			protocolSelectorLastTouchY.current = e.touches[0].clientY;
		}
		e.stopPropagation();
	};

	const handleProtocolSelectorContentTouchMove = (e) => {
		const content = protocolSelectorContentRef.current;
		if (!content) {
			e.stopPropagation();
			return;
		}

		const { scrollTop, scrollHeight, clientHeight } = content;
		const isAtTop = scrollTop === 0;
		const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
		
		if (e.touches.length > 0 && protocolSelectorLastTouchY.current !== null) {
			const currentTouchY = e.touches[0].clientY;
			const deltaY = currentTouchY - protocolSelectorLastTouchY.current;
			protocolSelectorLastTouchY.current = currentTouchY;
			
			if ((deltaY > 0 && isAtTop) || (deltaY < 0 && isAtBottom)) {
				e.preventDefault();
				e.stopPropagation();
			} else {
				e.stopPropagation();
			}
		} else {
			e.stopPropagation();
		}
	};

	// Prevent body scroll when modal is open
	useEffect(() => {
		if (showReportModal || showProtocolSelectorCard) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [showReportModal, showProtocolSelectorCard]);

	// Group reports by month
	const groupReportsByMonth = (reports) => {
		if (!reports || reports.length === 0) return [];
		
		const groups = [];
		let currentGroup = null;
		
		reports.forEach(report => {
			const reportDate = new Date(report.created_at);
			const monthKey = `${reportDate.getFullYear()}-${reportDate.getMonth()}`;
			const monthName = reportDate.toLocaleDateString('en-US', { month: 'long' });
			
			if (!currentGroup || currentGroup.monthKey !== monthKey) {
				currentGroup = {
					monthKey,
					monthName,
					reports: []
				};
				groups.push(currentGroup);
			}
			
			currentGroup.reports.push(report);
		});
		
		return groups;
	};

	// Get unique days from reports
	const getUniqueDays = (reports) => {
		if (!reports || reports.length === 0) return [];
		
		const dayMap = new Map();
		
		reports.forEach((report, index) => {
			const reportDate = new Date(report.created_at);
			const dayKey = reportDate.toISOString().split('T')[0]; // YYYY-MM-DD format
			
			if (!dayMap.has(dayKey)) {
				dayMap.set(dayKey, {
					date: reportDate,
					reportIndex: index, // Store the index of the first report on this day
					report: report
				});
			}
		});
		
		return Array.from(dayMap.values());
	};

	// Handle FAB menu toggle
	const handleFabButtonClick = (e, reportId) => {
		e.stopPropagation();
		setOpenFabMenuId(openFabMenuId === reportId ? null : reportId);
	};

	// Close FAB menu when clicking outside
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (
				openFabMenuId &&
				!e.target.closest('.report-cover-fab-menu') &&
				!e.target.closest('.report-cover-fab-btn')
			) {
				setOpenFabMenuId(null);
			}
		};

		if (openFabMenuId) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [openFabMenuId]);

	// Close FAB menu on Escape key
	useEffect(() => {
		const handleEscape = (e) => {
			if (e.key === 'Escape') {
				if (openFabMenuId) {
					setOpenFabMenuId(null);
				}
				if (showProtocolSelectorCard) {
					setShowProtocolSelectorCard(false);
				}
			}
		};

		if (openFabMenuId || showProtocolSelectorCard) {
			document.addEventListener('keydown', handleEscape);
		}

		return () => {
			document.removeEventListener('keydown', handleEscape);
		};
	}, [openFabMenuId, showProtocolSelectorCard]);

	// Handle delete report confirmation
	const handleDeleteClick = (e, reportId) => {
		e.stopPropagation();
		setReportToDelete(reportId);
		setShowDeleteConfirm(true);
		setOpenFabMenuId(null); // Close FAB menu
	};

	// Handle delete report
	const handleDeleteReport = async () => {
		if (!reportToDelete) return;

		try {
			// Use getSession() instead of getUser() to avoid network requests
			const { data: { session } } = await supabase.auth.getSession();
			const user = session?.user;
			if (!user) {
				alert('You must be signed in to delete reports.');
				setShowDeleteConfirm(false);
				setReportToDelete(null);
				return;
			}

			// Delete from protocol_report_snapshots table
			const { error } = await supabase
				.from('protocol_report_snapshots')
				.delete()
				.eq('id', reportToDelete)
				.eq('user_id', user.id); // Ensure user can only delete their own reports

			if (error) {
				console.error('Error deleting report:', error);
				alert(getUserFacingErrorMessage(error, 'Failed to delete report. Please try again.'));
				setShowDeleteConfirm(false);
				setReportToDelete(null);
				return;
			}

			// Close confirmation modal
			setShowDeleteConfirm(false);
			setReportToDelete(null);

			// Reload reports to reflect the deletion
			loadData();
		} catch (err) {
			console.error('Exception deleting report:', err);
			alert('An error occurred while deleting the report.');
			setShowDeleteConfirm(false);
			setReportToDelete(null);
		}
	};

	// Handle cancel delete
	const handleCancelDelete = () => {
		setShowDeleteConfirm(false);
		setReportToDelete(null);
	};

	// Calculate card positions relative to timeline
	const updateCardPositions = () => {
		const rowElement = reportRowRef.current;
		if (!rowElement) return;

		const positions = {};
		const rowRect = rowElement.getBoundingClientRect();
		const rowLeft = rowRect.left;

		reports.forEach((report) => {
			const cardElement = cardRefs.current[report.id];
			if (cardElement) {
				const cardRect = cardElement.getBoundingClientRect();
				const cardCenter = cardRect.left + (cardRect.width / 2) - rowLeft;
				positions[report.id] = cardCenter;
			}
		});

		setCardPositions(positions);
	};

	// Handle scroll sync for timeline
	const handleReportScroll = (e) => {
		const element = e.target;
		const scrollLeft = element.scrollLeft;
		const scrollWidth = element.scrollWidth;
		const clientWidth = element.clientWidth;
		const maxScroll = Math.max(0, scrollWidth - clientWidth);
		const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
		setScrollProgress(progress);
		updateCardPositions();
	};

	// Sync timeline with scroll on mount and when reports change
	useEffect(() => {
		const rowElement = reportRowRef.current;
		if (rowElement && reports.length > 0) {
			rowElement.addEventListener('scroll', handleReportScroll);
			// Initial calculation
			handleReportScroll({ target: rowElement });
			updateCardPositions();
			
			// Update positions on resize
			const resizeObserver = new ResizeObserver(() => {
				updateCardPositions();
			});
			resizeObserver.observe(rowElement);
			
			return () => {
				rowElement.removeEventListener('scroll', handleReportScroll);
				resizeObserver.disconnect();
			};
		}
	}, [reports.length]);

	// Close tips modal when clicking outside
	useEffect(() => {
		if (showTipsModal) {
			const handleClickOutside = (event) => {
				if (!event.target.closest('.insights-tips-modal') && 
						!event.target.closest('.insights-tips-button')) {
					setShowTipsModal(false);
				}
			};
			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [showTipsModal]);

	return (
		<div className="insights-page">
			<TopNavigation activePage="insights" />
			
			<div className="insights-container">
				{/* Create Report/Create Protocol FAB Button */}
				<div className="create-report-fab-container">
					{/* Loading Spinner */}
					{isCreatingReport && (
						<div className="create-report-loading">
							<div className="create-report-loading-spinner"></div>
						</div>
					)}
					{!loading && protocolOptions.length === 0 ? (
						<button
							className="create-report-fab-button"
							onClick={() => navigate('/protocols/create')}
							aria-label="Create protocol"
						>
							Create Protocol
						</button>
					) : (
						<button
							className="create-report-fab-button"
							onClick={handleCreateFabButtonClick}
							disabled={loading || protocolOptions.length === 0 || isCreatingReport}
							aria-label="Create report"
							aria-expanded={showProtocolSelectorCard}
						>
							Create Report
						</button>
					)}
					{!loading && protocolOptions.length === 0 && (
						<p className="create-report-helper-text">Create a protocol before you can create a report</p>
					)}
				</div>

				{/* Report History - Fixed Bottom Scrollable Row */}
				{!loading && (
					<div className="report-history-bottom">
						<div className="report-history-divider" />
						{/* Report History Title */}
						<div className="report-history-header">
							<div className="report-history-title">Report History</div>
							<div className="report-history-subtitle">Browse through all your saved reports</div>
						</div>
						{/* Sticky Timeline */}
						<div className="report-timeline">
							<div className="report-timeline-line">
								{/* Tick marks */}
								{Array.from({ length: 20 }).map((_, i) => (
									<div 
										key={i} 
										className="report-timeline-tick"
										style={{ left: `${(i / 19) * 100}%` }}
									/>
								))}
							</div>
							{reports.length > 0 && (
								<div className="report-timeline-marks">
									{(() => {
										const uniqueDays = getUniqueDays(reports);
										const totalDays = uniqueDays.length;
										const totalReports = reports.length;
										
										return uniqueDays.map((dayData, dayIndex) => {
											const reportDate = dayData.date;
											const month = reportDate.toLocaleDateString('en-US', { month: 'short' });
											const day = reportDate.getDate();
											const isFirst = dayIndex === 0;
											const isLast = dayIndex === totalDays - 1;
											
											// Get the actual card position for the first report on this day
											const cardPosition = cardPositions[dayData.report.id];
											const rowElement = reportRowRef.current;
											
											// Calculate position as percentage of timeline width
											let leftPosition = 50; // default center
											
											if (cardPosition !== undefined && rowElement) {
												// Get the timeline marks container width (same as row width, no padding)
												const rowRect = rowElement.getBoundingClientRect();
												const timelineWidth = rowRect.width;
												
												// Shift a few pixels to the left for better alignment
												const leftOffset = 5; // pixels to shift left
												const adjustedCardPosition = cardPosition - leftOffset;
												
												// Convert card center position to percentage of timeline width
												// cardPosition is already relative to row left, accounting for padding
												leftPosition = (adjustedCardPosition / timelineWidth) * 100;
											} else {
												// Fallback: use index-based positioning
												leftPosition = totalReports > 1 
													? (dayData.reportIndex / (totalReports - 1)) * 100
													: 50;
											}
											
											return (
												<div 
													key={`${reportDate.toISOString().split('T')[0]}-${dayIndex}`}
													className="report-timeline-mark"
													style={{ 
														left: `${leftPosition}%`,
														transform: 'translateX(-50%)'
													}}
												>
													<div className="report-timeline-dot" />
													<div className={`report-timeline-label ${isFirst ? 'first' : ''} ${isLast ? 'last' : ''}`}>
														<span className="report-timeline-month">{month}</span>
														<span className="report-timeline-day">{day}</span>
													</div>
												</div>
											);
										});
									})()}
								</div>
							)}
						</div>
						{reports.length > 0 ? (
							<div 
								className="report-covers-row" 
								ref={reportRowRef}
								onScroll={handleReportScroll}
							>
								{reports.map((report, index) => {
									const reportDate = new Date(report.created_at);
									const producedDate = reportDate.toLocaleDateString('en-US', {
										month: 'long',
										day: 'numeric',
										year: 'numeric'
									});
									const titleText = report.protocols?.name || report.title || 'Protocol Report';
									
									return (
										<div
											key={report.id}
											ref={(el) => {
												if (el) cardRefs.current[report.id] = el;
											}}
											className="report-cover-thumbnail"
											onClick={() => {
												if (openFabMenuId !== report.id) {
													handleViewReport(report.id);
												}
											}}
											style={{ zIndex: reports.length - index }}
										>
											<div className="report-cover-thumbnail-inner">
												<div className="report-cover-thumbnail-fade" />
												
												{/* FAB Menu Button */}
												<button
													className="report-cover-fab-btn"
													onClick={(e) => handleFabButtonClick(e, report.id)}
													title="More options"
													aria-label="Open report menu"
													aria-expanded={openFabMenuId === report.id}
												>
													<Icon name="MoreVertical" className="report-cover-fab-icon" />
												</button>

												{/* FAB Menu */}
												{openFabMenuId === report.id && (
													<div 
														className="report-cover-fab-menu"
														role="menu"
														aria-orientation="vertical"
													>
														<button
															className="report-cover-fab-menu-item"
															onClick={(e) => handleDeleteClick(e, report.id)}
															role="menuitem"
														>
															<Icon name="Trash2" className="report-cover-fab-menu-icon" />
															<span>Delete</span>
														</button>
													</div>
												)}
												
												{userProfile.avatarUrl ? (
													<img 
														className="report-cover-thumbnail-avatar" 
														src={userProfile.avatarUrl} 
														alt="profile" 
													/>
												) : (
													<div className="report-cover-thumbnail-avatar fallback">
														{(userProfile.userName || 'U').slice(0, 1).toUpperCase()}
													</div>
												)}
												{userProfile.userName && (
													<div className="report-cover-thumbnail-username">{userProfile.userName}</div>
												)}
												<div className="report-cover-thumbnail-title">{titleText}</div>
												<div className="report-cover-thumbnail-subtitle">Created {producedDate}</div>
											</div>
										</div>
									);
								})}
							</div>
						) : null}
					</div>
				)}
			</div>

			{/* Insights onboarding is now handled by the main Onboarding component */}

			{/* Report Modal Overlay */}
			{showReportModal && (
				<div className="report-modal-overlay" onClick={handleCloseModal}>
					<div className="report-modal-content" onClick={(e) => e.stopPropagation()}>
						{modalReportId === 'example' ? (
							<ExampleReportWrapper onClose={handleCloseModal} />
						) : (
							<ProtocolReport 
								reportId={modalReportId}
								protocolId={modalProtocolId}
								isModal={true}
								onClose={handleCloseModal}
							/>
						)}
					</div>
				</div>
			)}

			{/* Delete Confirmation Modal */}
			{showDeleteConfirm && (
				<div className="report-modal-overlay" onClick={handleCancelDelete}>
					<div className="report-modal" onClick={(e) => e.stopPropagation()}>
						<div className="report-modal-header">
							<div className="report-modal-title">
								<Icon name="AlertTriangle" className="report-modal-icon" />
								<h2>Delete Report</h2>
							</div>
							<button className="report-modal-close" onClick={handleCancelDelete}>
								<Icon name="X" className="icon--sm" />
							</button>
						</div>

						<div className="report-modal-content">
							<div className="report-target-info" style={{ borderBottom: 'none', paddingBottom: 0 }}>
								<p>Are you sure you want to delete this report? This action cannot be undone.</p>
							</div>

							<div className="report-modal-actions">
								<button
									type="button"
									className="btn btn--tertiary"
									onClick={handleCancelDelete}
								>
									Cancel
								</button>
								<button
									type="button"
									className="btn btn--danger"
									onClick={handleDeleteReport}
								>
									Delete
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Protocol Selector Card */}
			{showProtocolSelectorCard && (
				<div 
					className="attachment-selector-overlay" 
					onClick={handleProtocolSelectorOverlayClick}
					onWheel={handleProtocolSelectorOverlayWheel}
					onTouchMove={(e) => {
						if (e.target === e.currentTarget) {
							e.preventDefault();
							e.stopPropagation();
						}
					}}
				>
					<div className="attachment-selector" onClick={(e) => e.stopPropagation()}>
						<div className="attachment-selector-header">
							<div className="attachment-selector-title">
								<Icon name="TrendingUp" className="icon--sm" />
								<span>Create Report</span>
							</div>
							<div className="attachment-selector-actions">
								<button 
									className="attachment-search-toggle"
									onClick={() => setShowProtocolSelectorSearch(!showProtocolSelectorSearch)}
								>
									<Icon name="Search" className="icon--sm" />
								</button>
								<button className="attachment-selector-close" onClick={handleProtocolSelectorClose}>
									<Icon name="X" className="icon--sm" />
								</button>
							</div>
						</div>

						{showProtocolSelectorSearch && (
							<div className="attachment-selector-search">
								<input
									type="text"
									placeholder="Search protocols..."
									value={protocolSelectorSearchTerm}
									onChange={(e) => setProtocolSelectorSearchTerm(e.target.value)}
									className="attachment-search-input"
									autoFocus
								/>
								<button 
									className="attachment-search-close"
									onClick={() => {
										setShowProtocolSelectorSearch(false);
										setProtocolSelectorSearchTerm('');
									}}
								>
									<Icon name="X" className="icon--xs" />
								</button>
							</div>
						)}

						<div 
							ref={protocolSelectorContentRef}
							className="attachment-selector-content" 
							onWheel={handleProtocolSelectorContentWheel} 
							onTouchStart={handleProtocolSelectorContentTouchStart}
							onTouchMove={handleProtocolSelectorContentTouchMove}
						>
							{loadingProtocols ? (
								<div className="attachment-selector-loading">
									<JellyfishLoader />
								</div>
							) : protocolsForSelector.length === 0 ? (
								<div className="attachment-selector-empty">
									<Icon name="TrendingUp" className="icon--md" />
									<span>No protocols found</span>
								</div>
							) : (() => {
								const filteredProtocols = protocolsForSelector.filter(protocol =>
									protocol.name.toLowerCase().includes(protocolSelectorSearchTerm.toLowerCase()) ||
									protocol.description?.toLowerCase().includes(protocolSelectorSearchTerm.toLowerCase())
								);

								return filteredProtocols.length === 0 ? (
									<div className="attachment-selector-empty">
										<Icon name="TrendingUp" className="icon--md" />
										<span>No protocols found</span>
									</div>
								) : (
									<div className="protocol-selector-shields-grid">
										{filteredProtocols.map(protocol => (
											<div
												key={protocol.id}
												className="protocol-selector-shield-item"
												onClick={() => handleProtocolSelect(protocol)}
											>
												<ProtocolShield
													protocol={protocol}
													size="medium"
													onBiohackClick={setSelectedBiohack}
													disableFlip={true}
													hideFabMenu={true}
												/>
											</div>
										))}
									</div>
								);
							})()}
						</div>
					</div>

					{/* Biohack Info Card Modal */}
					{selectedBiohack && (
						<BiohackInfoCard
							biohack={selectedBiohack}
							onClose={() => setSelectedBiohack(null)}
							onRatingSubmitted={() => {}}
						/>
					)}
				</div>
			)}

			{/* Floating Tips Button */}
			<button
				className={`insights-tips-button ${showProtocolSelectorCard ? 'hidden-on-mobile' : ''}`}
				onClick={() => setShowTipsModal(!showTipsModal)}
				title="Tips"
			>
				<Icon name="Info" />
				<span>Tips</span>
			</button>

			{/* Tips Modal */}
			{showTipsModal && (
				<div className="insights-tips-modal-overlay" onClick={() => setShowTipsModal(false)}>
					<div className="insights-tips-modal" onClick={(e) => e.stopPropagation()}>
						<div className="insights-tips-modal-header">
							<h3>Tips</h3>
							<button
								className="insights-tips-modal-close"
								onClick={() => setShowTipsModal(false)}
								aria-label="Close tips"
							>
								<Icon name="X" />
							</button>
						</div>
						<div className="insights-tips-modal-content">
							<p>Track your protocol progress, view detailed reports, and analyze your biohacking journey over time</p>
							<div className="insights-tips-details">
								<h4>What you can do:</h4>
								<ul>
									<li><strong>Create Reports:</strong> Click the "Create Report" button to generate a comprehensive snapshot of your protocol progress for the past 28 days, including logging data, adherence metrics, and biohack activity</li>
									<li><strong>View Report History:</strong> Scroll through the timeline below to browse all your saved reports, organized chronologically with date markers</li>
									<li><strong>Open Detailed Reports:</strong> Click on any report card to open a full-screen view with detailed metrics, biomarkers, biohacks, and protocol adherence data</li>
									<li><strong>Track Key Metrics:</strong> Each report displays Total Logs, Active Days, Adherence Percentage, and Biohack Count to measure your consistency and progress</li>
									<li><strong>Manage Reports:</strong> Use the three-dot menu on any report card to delete reports you no longer need</li>
									<li><strong>Select Protocols:</strong> When creating a report, choose from your active protocols using the protocol selector - you can search and filter to find the protocol you want</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Protocol Report Creation Disclaimer Modal */}
			{showDisclaimerModal && (
				<div className="modal-overlay" onClick={(e) => {
					// Don't close on overlay click - user must explicitly choose
					e.stopPropagation();
				}}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
						<div className="modal-header">
							<h3>Protocol Report Creation Disclaimer</h3>
						</div>
						<div className="modal-body" style={{ padding: '24px' }}>
							<div style={{ 
								marginBottom: '24px', 
								fontSize: '14px', 
								lineHeight: '1.6', 
								color: 'var(--color-text-primary)',
								whiteSpace: 'pre-line'
							}}>
								{disclaimerText}
							</div>
							
							<div style={{ marginBottom: '20px' }}>
								<label style={{ 
									display: 'flex', 
									alignItems: 'flex-start', 
									gap: '12px', 
									cursor: 'pointer',
									fontSize: '14px',
									lineHeight: '1.5',
									color: 'var(--color-text-primary)'
								}}>
									<input
										type="checkbox"
										checked={acceptResponsibility}
										onChange={(e) => setAcceptResponsibility(e.target.checked)}
										style={{ 
											marginTop: '2px',
											width: '18px',
											height: '18px',
											cursor: 'pointer',
											flexShrink: 0
										}}
									/>
									<span>I understand and accept responsibility for creating this report</span>
								</label>
							</div>

							<div style={{ marginBottom: '24px' }}>
								<label style={{ 
									display: 'flex', 
									alignItems: 'flex-start', 
									gap: '12px', 
									cursor: 'pointer',
									fontSize: '14px',
									lineHeight: '1.5',
									color: 'var(--color-text-primary)'
								}}>
									<input
										type="checkbox"
										checked={doNotShowAgain}
										onChange={(e) => setDoNotShowAgain(e.target.checked)}
										style={{ 
											marginTop: '2px',
											width: '18px',
											height: '18px',
											cursor: 'pointer',
											flexShrink: 0
										}}
									/>
									<span>Do not show again</span>
								</label>
							</div>

							<div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
								<button
									type="button"
									className="btn btn--secondary btn--md btn--rounded"
									onClick={handleDisclaimerCancel}
									style={{ flex: 1 }}
								>
									Cancel
								</button>
								<button
									type="button"
									className="btn btn--primary btn--md btn--rounded"
									onClick={handleDisclaimerAccept}
									disabled={!acceptResponsibility}
									style={{ flex: 1 }}
								>
									Accept
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			<BottomNavigation />
			{showOnboarding && (
				<Onboarding onComplete={() => setShowOnboarding(false)} />
			)}
		</div>
	);
};

export default InsightsIndex;
